import { TF } from 'react-translator-component';

import { CrossTransaction } from '../../../types/responses/Interoperability';

/**
 * Formatação da aba Transações (MV2-1717).
 *
 * Fora do componente porque é regra, não marcação: duração de recarga em
 * andamento, rótulo de estação que repete o código, e número que precisa sair
 * na vírgula decimal do país.
 */

const pad = (value: number): string => String(value).padStart(2, '0');

/**
 * Rótulo da estação.
 *
 * No banco a descrição costuma repetir o código; mostrar a mesma string em duas
 * linhas não informa nada. Quando são iguais sobra só o código, que é o que
 * identifica sem ambiguidade.
 */
export const stationLabel = (
  charge: CrossTransaction,
): { title: string; subtitle: string | null } => {
  const code = charge.charge_box_code;
  const name = charge.charge_box_name;

  if (!code && !name) {
    // Estação removida depois da recarga: a linha continua valendo para
    // faturamento, então não pode ficar em branco sem explicação.
    return { title: TF('Station no longer registered'), subtitle: null };
  }

  if (!name || name === code) {
    return { title: code as string, subtitle: null };
  }

  return { title: name, subtitle: code };
};

/**
 * Duração da recarga em hh:mm:ss.
 *
 * Recarga em andamento conta até agora, e é por isso que `now` entra como
 * parâmetro: sem ele a função não seria testável nem estável dentro de um
 * mesmo render.
 */
export const chargeDuration = (
  charge: CrossTransaction,
  now: Date = new Date(),
): string => {
  if (!charge.start_timestamp) {
    return '—';
  }

  const start = new Date(charge.start_timestamp).getTime();
  const end = charge.stop_timestamp ? new Date(charge.stop_timestamp).getTime() : now.getTime();

  if (Number.isNaN(start) || Number.isNaN(end) || end < start) {
    return '—';
  }

  const totalSeconds = Math.floor((end - start) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const shortMoment = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return '';
  }

  return `${pad(moment.getDate())}/${pad(moment.getMonth() + 1)} ${pad(moment.getHours())}:${pad(
    moment.getMinutes(),
  )}`;
};

/** "17/08 15:17 - 17/08 16:46", ou "- Carregando..." quando não terminou. */
export const chargePeriod = (charge: CrossTransaction): string => {
  const start = shortMoment(charge.start_timestamp);

  if (!start) {
    return '—';
  }

  return `${start} - ${charge.stop_timestamp ? shortMoment(charge.stop_timestamp) : TF('Charging...')}`;
};

/** Energia com três decimais, como o medidor grava. */
export const formatEnergy = (value: number | null): string =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

/**
 * Valor monetário.
 *
 * Sem símbolo fixo de moeda: o módulo atende tenants em BRL, COP e USD, e
 * escrever "R$" na tela mentiria para os outros.
 */
export const formatMoney = (value: number | null): string =>
  value === null || value === undefined
    ? '—'
    : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatCount = (value: number | null): string =>
  value === null || value === undefined ? '—' : value.toLocaleString();

/**
 * Intervalo padrão da tela: últimos sete dias, como no protótipo.
 *
 * Devolve no formato que o @DateTimeFormat do Spring aceita — data e hora
 * locais, sem fuso. Mandar com Z faria o servidor ler outra hora.
 */
export const defaultPeriod = (now: Date = new Date()): { from: string; to: string } => {
  const start = new Date(now);
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setHours(23, 59, 59, 0);

  const iso = (moment: Date): string =>
    `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}T${pad(
      moment.getHours(),
    )}:${pad(moment.getMinutes())}:${pad(moment.getSeconds())}`;

  return { from: iso(start), to: iso(end) };
};

/**
 * O período filtrado como a API o exige (MV2-1706).
 *
 * O estado das telas guarda só a data (`YYYY-MM-DD`), que é o que o RangePicker escolhe. A API
 * recebe `LocalDateTime` com `@DateTimeFormat(ISO.DATE_TIME)`: data sem hora devolve **400** —
 * conferido por HTTP contra o serviço.
 *
 * O dia final vai até 23:59:59 porque a consulta compara `startTimestamp <= :to`. Com 00:00:00,
 * o último dia escolhido sairia do resultado sem ninguém notar.
 *
 * Existe como função porque as duas telas que filtram por período — Transações e Auditoria —
 * repetiam as mesmas duas linhas, e porque uma revisão apontou este ponto como defeito: a
 * concatenação estava correta, mas espalhada e sem teste que a fixasse.
 */
export const periodQuery = (
  from: string,
  to: string,
): { from?: string; to?: string } => ({
  ...(from ? { from: `${from}T00:00:00` } : {}),
  ...(to ? { to: `${to}T23:59:59` } : {}),
});
